/**
 * Custom element for handling product variant selection in a sidebar drawer.
 * Listens for changes on variant selection inputs and publishes events with the selected variant details.
 *
 * @class DrawerVariantSelect
 * @extends HTMLElement
 */

if (!customElements.get("drawer-variant-select")) {
  class DrawerVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.DEV = false;
      this.productId = this.dataset.productId;
    }

    connectedCallback() {
      this.#log("connectedCallback");
      this.form = this.querySelector("[data-variant-form]");
      if (!this.form) return;

      this.inputs = this.form.querySelectorAll(
        'input[type="radio"], input[type="checkbox"], select'
      );
      this.inputs.forEach((input) => {
        input.addEventListener("change", (e) => this.#onVariantChange(e));
      });
      subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
        if (data.productId != this.productId) return;
        if (data.area !== "pdp") return;

        this.#log("Syncing variant from PDP", data.variantId);
        this.#syncVariantSelection(data.variantId);
      });
    }

    /**
     * Handles variant input change events.
     * Updates checked state, logs the change, and publishes a variant change event.
     *
     * @method #onVariantChange
     * @memberof DrawerVariantSelect
     * @param {Event} e - The change event from the input.
     */
    #onVariantChange(e) {
      const selectedInput = e.target;

      this.form
        .querySelectorAll(`input[name="${selectedInput.name}"]`)
        .forEach((input) => {
          input.removeAttribute("checked");
        });

      selectedInput.setAttribute("checked", "checked");
      const variantId = selectedInput.dataset.variantId;
      const colorDesc = selectedInput.dataset.color;
      const inputValue = selectedInput.dataset.value;
      const label = selectedInput.closest("label");
      const targetColor = label?.querySelector("[data-target-color]");


      this.#log("Variant changed", { variantId }, "On product", this.productId);

      this.#log("this.currentVariant", selectedInput);
      publish(PUB_SUB_EVENTS.variantChange, {
        source: "drawer-variant-select",
        variantId: variantId,
        productId: this.productId,
        colorDescription: colorDesc,
        inputValue: inputValue,
        targetColor: targetColor,
        area: "drawer",
      });
      setTimeout(() => {
        this.#closeDrawer();
      }, 350);
    }

    /**
     * Closes the parent sidebar drawer if open.
     *
     * @method #closeDrawer
     * @memberof DrawerVariantSelect
     */
    #closeDrawer() {
      const openedDrawer = this.closest("sidebar-drawer.sidebar--opened");
      if (openedDrawer && typeof openedDrawer.hide === "function") {
        this.#log("Closing drawer after variant change");
        openedDrawer.hide();
      }
    }

    /**
     * Synchronizes the checked state of variant selection inputs based on the provided variant ID.
     * Sets the matching input as checked and unchecks all others.
     *
     * @param {string|number} variantId - The ID of the variant to select.
     */
    #syncVariantSelection(variantId) {
      this.inputs.forEach((input) => {
        if (input.dataset.variantId === String(variantId)) {
          input.checked = true;
          input.setAttribute("checked", "checked");
        } else {
          input.checked = false;
          input.removeAttribute("checked");
        }
      });
    }

    /**
     * Logs debug information to the console if DEV mode is enabled.
     *
     * @method #log
     * @memberof DrawerVariantSelect
     * @param {...any} args - Arguments to log.
     */
    #log() {
      if (this.DEV) console.log(this.constructor.name, ...arguments);
    }
  }

  customElements.define("drawer-variant-select", DrawerVariantSelect);
}